import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import Divider from "@mui/material/Divider";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutlineOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutlineOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import { PageHeader } from "@/components/PageHeader";
import { useAuth } from "@/hooks/useAuth";
import { useCandidate } from "@/context/CandidateContext";
import {
  fetchGoals,
  goalsQueryKey,
  updateGoalStatus,
  deleteGoal,
  type GoalRow,
  type GoalStatus,
} from "@/lib/queries/goals";

export const Route = createFileRoute("/app/goals")({
  beforeLoad: () => {
    throw redirect({ to: "/app/tools", search: { tab: "goals" } });
  },
  component: () => null,
});

export function GoalsPanel({ hideHeader = false }: { hideHeader?: boolean } = {}) {
  const { user } = useAuth();
  const { profile } = useCandidate();
  const userId = user?.id;
  const queryClient = useQueryClient();

  const verifiedSkillNames = useMemo(
    () => new Set(profile.verifiedSkills.map((s) => s.name.toLowerCase())),
    [profile.verifiedSkills],
  );

  const { data: goals, isLoading } = useQuery({
    queryKey: userId ? goalsQueryKey(userId) : ["goals", "anon"],
    queryFn: () => fetchGoals(userId as string),
    enabled: !!userId,
  });

  const statusMutation = useMutation({
    mutationFn: (args: { id: string; status: GoalStatus }) =>
      updateGoalStatus(args.id, args.status),
    onSuccess: () => {
      if (userId) queryClient.invalidateQueries({ queryKey: goalsQueryKey(userId) });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteGoal(id),
    onSuccess: () => {
      if (userId) queryClient.invalidateQueries({ queryKey: goalsQueryKey(userId) });
    },
  });

  const [confirmDelete, setConfirmDelete] = useState<GoalRow | null>(null);

  const grouped = useMemo(() => {
    const out: Record<GoalStatus, GoalRow[]> = { active: [], paused: [], achieved: [] };
    for (const g of goals ?? []) out[g.status].push(g);
    return out;
  }, [goals]);

  return (
    <Box>
      {!hideHeader && (
        <PageHeader
          title="Career goals"
          subtitle="Track the roles you're aiming at and your skill-readiness for each."
          actions={
            <Button
              component={Link}
              to="/app/tools"
              search={{ tab: "career-path" } as never}
              variant="contained"
              startIcon={<FlagOutlinedIcon />}
            >
              Add a goal
            </Button>
          }
        />
      )}

      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!isLoading && (goals?.length ?? 0) === 0 && (
        <Card>
          <CardContent>
            <Stack spacing={2} sx={{ alignItems: "center", py: 6 }}>
              <FlagOutlinedIcon sx={{ fontSize: 48, color: "text.disabled" }} />
              <Typography variant="h6">No goals yet</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", maxWidth: 420 }}>
                Explore the Career Path graph to find target roles, then set them as goals to
                track which skills you still need to learn.
              </Typography>
              <Button component={Link} to="/app/career-path" variant="contained">
                Explore career paths
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {!isLoading && (goals?.length ?? 0) > 0 && (
        <Stack spacing={4}>
          <GoalSection
            title="Active"
            icon={<FlagOutlinedIcon color="primary" />}
            goals={grouped.active}
            verifiedSkills={verifiedSkillNames}
            onStatusChange={(id, status) => statusMutation.mutate({ id, status })}
            onDelete={(g) => setConfirmDelete(g)}
          />
          {grouped.paused.length > 0 && (
            <GoalSection
              title="Paused"
              icon={<PauseCircleOutlineIcon color="warning" />}
              goals={grouped.paused}
              verifiedSkills={verifiedSkillNames}
              onStatusChange={(id, status) => statusMutation.mutate({ id, status })}
              onDelete={(g) => setConfirmDelete(g)}
            />
          )}
          {grouped.achieved.length > 0 && (
            <GoalSection
              title="Achieved"
              icon={<EmojiEventsOutlinedIcon color="success" />}
              goals={grouped.achieved}
              verifiedSkills={verifiedSkillNames}
              onStatusChange={(id, status) => statusMutation.mutate({ id, status })}
              onDelete={(g) => setConfirmDelete(g)}
            />
          )}
        </Stack>
      )}

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Delete this goal?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will permanently remove "{confirmDelete?.role}" from your goals.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              if (confirmDelete) deleteMutation.mutate(confirmDelete.id);
              setConfirmDelete(null);
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function GoalSection({
  title,
  icon,
  goals,
  verifiedSkills,
  onStatusChange,
  onDelete,
}: {
  title: string;
  icon: React.ReactNode;
  goals: GoalRow[];
  verifiedSkills: Set<string>;
  onStatusChange: (id: string, status: GoalStatus) => void;
  onDelete: (g: GoalRow) => void;
}) {
  if (goals.length === 0) {
    return (
      <Box>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 1.5 }}>
          {icon}
          <Typography variant="h6">{title}</Typography>
        </Stack>
        <Alert severity="info">No {title.toLowerCase()} goals.</Alert>
      </Box>
    );
  }
  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 1.5 }}>
        {icon}
        <Typography variant="h6">{title}</Typography>
        <Chip label={goals.length} size="small" />
      </Stack>
      <Stack spacing={2}>
        {goals.map((g) => (
          <GoalCard
            key={g.id}
            goal={g}
            verifiedSkills={verifiedSkills}
            onStatusChange={onStatusChange}
            onDelete={onDelete}
          />
        ))}
      </Stack>
    </Box>
  );
}

function GoalCard({
  goal,
  verifiedSkills,
  onStatusChange,
  onDelete,
}: {
  goal: GoalRow;
  verifiedSkills: Set<string>;
  onStatusChange: (id: string, status: GoalStatus) => void;
  onDelete: (g: GoalRow) => void;
}) {
  const required = goal.required_skills ?? [];
  const matched = required.filter((s) => verifiedSkills.has(s.toLowerCase()));
  const missing = required.filter((s) => !verifiedSkills.has(s.toLowerCase()));
  const pct = required.length === 0 ? 0 : Math.round((matched.length / required.length) * 100);

  return (
    <Card>
      <CardContent>
        <Stack direction="row" spacing={2} sx={{ alignItems: "flex-start", justifyContent: "space-between" }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
              <Typography variant="h6">{goal.role}</Typography>
              {goal.category && <Chip size="small" label={goal.category} variant="outlined" />}
              {goal.salary_band && <Chip size="small" label={goal.salary_band} color="primary" variant="outlined" />}
            </Stack>
            {goal.rationale && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {goal.rationale}
              </Typography>
            )}
          </Box>
          <Stack direction="row" spacing={0.5}>
            {goal.status === "active" && (
              <Tooltip title="Pause">
                <IconButton size="small" onClick={() => onStatusChange(goal.id, "paused")}>
                  <PauseCircleOutlineIcon />
                </IconButton>
              </Tooltip>
            )}
            {goal.status === "paused" && (
              <Tooltip title="Resume">
                <IconButton size="small" onClick={() => onStatusChange(goal.id, "active")}>
                  <PlayCircleOutlineIcon />
                </IconButton>
              </Tooltip>
            )}
            {goal.status !== "achieved" && (
              <Tooltip title="Mark as achieved">
                <IconButton size="small" onClick={() => onStatusChange(goal.id, "achieved")}>
                  <EmojiEventsOutlinedIcon />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Delete">
              <IconButton size="small" onClick={() => onDelete(goal)}>
                <DeleteOutlineIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        {required.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 1 }}>
              <Typography variant="subtitle2">Skill readiness</Typography>
              <Typography variant="body2" color="text.secondary">
                {matched.length} / {required.length} verified
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Chip size="small" label={`${pct}%`} color={pct >= 75 ? "success" : pct >= 40 ? "primary" : "warning"} />
            </Stack>
            <LinearProgress
              variant="determinate"
              value={pct}
              color={pct >= 75 ? "success" : pct >= 40 ? "primary" : "warning"}
              sx={{ height: 8, borderRadius: 4, mb: 2 }}
            />
            <Stack direction="row" spacing={0.75} sx={{ flexWrap: "wrap" }} useFlexGap>
              {matched.map((s) => (
                <Chip key={`m-${s}`} size="small" label={s} color="success" variant="outlined" />
              ))}
              {missing.map((s) => (
                <Chip key={`x-${s}`} size="small" label={s} color="default" variant="outlined" sx={{ opacity: 0.7 }} />
              ))}
            </Stack>
            {missing.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: "block" }}>
                Missing {missing.length} skill{missing.length === 1 ? "" : "s"} — add them to your profile or focus
                your learning loop.
              </Typography>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
